import React, { useEffect, useMemo, useState } from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Tag } from "primereact/tag";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";
import {
  getHolidays,
  createHoliday,
  updateHoliday,
  deleteHoliday,
  resyncTermWeeks,
} from "../../api/holidayApi";

const emptyHoliday = {
  name: "",
  start_date: null,
  end_date: null,
  closes_school: true,
  note: "",
};

const toIso = (d) => {
  if (!d) return null;
  if (typeof d === "string") return d.slice(0, 10);
  // Calendar hands back a local-midnight Date; read local parts so the day
  // doesn't shift backwards for anyone east of UTC.
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const fromIso = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
};

const fmt = (s) =>
  fromIso(s)?.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }) || "";

/** Inclusive day span, for the "how long" column. */
const spanDays = (start, end) => {
  const a = fromIso(start);
  const b = fromIso(end);
  if (!a || !b) return 0;
  return Math.round((b - a) / 86400000) + 1;
};

/**
 * The school's holiday calendar.
 *
 * This is the input that term weeks are derived from — enter Eid or half term
 * once here and every affected week across every term marks itself partial or
 * closed, instead of the admin ticking week switches by hand.
 */
const HolidayCalendar = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyHoliday);
  const [saving, setSaving] = useState(false);
  const [resyncing, setResyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getHolidays();
      setHolidays(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load holidays");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyHoliday);
    setDialogOpen(true);
  };

  const openEdit = (h) => {
    setEditing(h);
    setForm({
      name: h.name || "",
      start_date: fromIso(h.start_date),
      end_date: fromIso(h.end_date),
      closes_school: h.closes_school !== false,
      note: h.note || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.warn("Give the holiday a name");
    if (!form.start_date) return toast.warn("Pick a start date");

    const payload = {
      name: form.name.trim(),
      start_date: toIso(form.start_date),
      // A single-day holiday just repeats the start date.
      end_date: toIso(form.end_date || form.start_date),
      closes_school: form.closes_school,
      note: form.note?.trim() || null,
    };

    setSaving(true);
    try {
      const res = editing
        ? await updateHoliday(editing.id, payload)
        : await createHoliday(payload);
      const touched = res.weeksUpdated || 0;
      toast.success(
        touched
          ? `Saved — ${touched} term week(s) recalculated`
          : "Saved"
      );
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save holiday");
    } finally {
      setSaving(false);
    }
  };

  const confirmRemove = (h) =>
    confirmDialog({
      header: "Remove holiday",
      message: `Remove "${h.name}"? Any term weeks it closed will reopen unless an admin pinned them off.`,
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          const res = await deleteHoliday(h.id);
          toast.success(
            res.weeksUpdated
              ? `Removed — ${res.weeksUpdated} term week(s) recalculated`
              : "Removed"
          );
          load();
        } catch (err) {
          toast.error(err?.response?.data?.message || "Failed to remove holiday");
        }
      },
    });

  const resync = async () => {
    setResyncing(true);
    try {
      const res = await resyncTermWeeks();
      toast.success(
        `Recalculated ${res.weeks} week(s) across ${res.terms} term(s) — ${res.changed} changed`
      );
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to recalculate");
    } finally {
      setResyncing(false);
    }
  };

  const { upcoming, closedDays } = useMemo(() => {
    const today = toIso(new Date());
    return {
      upcoming: holidays.filter((h) => String(h.end_date).slice(0, 10) >= today).length,
      closedDays: holidays
        .filter((h) => h.closes_school !== false)
        .reduce((sum, h) => sum + spanDays(h.start_date, h.end_date), 0),
    };
  }, [holidays]);

  return (
    <div className="p-4">
      <ConfirmDialog />

      <div className="flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
        <div>
          <h2 className="m-0 text-2xl font-bold">Holiday Calendar</h2>
          <p className="mt-1 mb-0 text-color-secondary" style={{ maxWidth: "44rem" }}>
            Enter each closure once. Term weeks read this calendar and mark themselves
            full, partial or closed automatically — you only touch a week by hand when
            something the calendar can't know about happens.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            label="Recalculate weeks"
            icon="pi pi-refresh"
            className="p-button-outlined"
            loading={resyncing}
            onClick={resync}
            tooltip="Reapply this calendar to every term"
          />
          <Button label="Add Holiday" icon="pi pi-plus" onClick={openNew} />
        </div>
      </div>

      <Card>
        <div className="flex flex-wrap gap-4 mb-3 text-sm text-color-secondary">
          <span><strong>{holidays.length}</strong> entries</span>
          <span><strong>{upcoming}</strong> current or upcoming</span>
          <span><strong>{closedDays}</strong> closure days total</span>
        </div>

        <DataTable
          value={holidays}
          loading={loading}
          dataKey="id"
          emptyMessage="No holidays yet — add your first closure to start driving term weeks."
          responsiveLayout="scroll"
          sortField="start_date"
          sortOrder={1}
        >
          <Column field="name" header="Name" sortable />
          <Column
            field="start_date"
            header="Dates"
            sortable
            body={(h) =>
              String(h.start_date).slice(0, 10) === String(h.end_date).slice(0, 10)
                ? fmt(h.start_date)
                : `${fmt(h.start_date)} – ${fmt(h.end_date)}`
            }
          />
          <Column
            header="Length"
            body={(h) => {
              const n = spanDays(h.start_date, h.end_date);
              return `${n} day${n === 1 ? "" : "s"}`;
            }}
          />
          <Column
            header="Effect"
            body={(h) =>
              h.closes_school !== false ? (
                <Tag severity="warning" value="Closes school" />
              ) : (
                <Tag severity="info" value="Note only" />
              )
            }
          />
          <Column field="note" header="Note" body={(h) => h.note || "—"} />
          <Column
            header=""
            style={{ width: "6rem" }}
            body={(h) => (
              <div className="flex gap-1">
                <Button
                  icon="pi pi-pencil"
                  className="p-button-text p-button-sm"
                  onClick={() => openEdit(h)}
                />
                <Button
                  icon="pi pi-trash"
                  className="p-button-text p-button-sm p-button-danger"
                  onClick={() => confirmRemove(h)}
                />
              </div>
            )}
          />
        </DataTable>
      </Card>

      <Dialog
        header={editing ? `Edit: ${editing.name}` : "Add Holiday"}
        visible={dialogOpen}
        style={{ width: "min(32rem, 95vw)" }}
        onHide={() => setDialogOpen(false)}
        modal
      >
        <div className="flex flex-column gap-3">
          <div>
            <label htmlFor="hName" className="block mb-1 font-semibold text-sm">Name</label>
            <InputText
              id="hName"
              className="w-full"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Eid al-Fitr, Half term, Founders Day"
            />
          </div>

          <div className="grid formgrid">
            <div className="col-6">
              <label htmlFor="hStart" className="block mb-1 font-semibold text-sm">Starts</label>
              <Calendar
                id="hStart"
                className="w-full"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.value })}
                dateFormat="dd M yy"
                showIcon
              />
            </div>
            <div className="col-6">
              <label htmlFor="hEnd" className="block mb-1 font-semibold text-sm">Ends</label>
              <Calendar
                id="hEnd"
                className="w-full"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.value })}
                minDate={form.start_date || undefined}
                dateFormat="dd M yy"
                showIcon
                placeholder="Same day"
              />
            </div>
          </div>
          <small className="text-color-secondary -mt-2">
            Both dates are included. Leave the end date empty for a single day.
          </small>

          <div className="flex align-items-start gap-2 p-3 surface-ground border-round">
            <Checkbox
              inputId="hCloses"
              checked={form.closes_school}
              onChange={(e) => setForm({ ...form, closes_school: e.checked })}
            />
            <label htmlFor="hCloses" className="cursor-pointer">
              <div className="font-semibold">School is closed</div>
              <small className="text-color-secondary">
                Shortens the term weeks it falls in. Untick for calendar notes like a
                sports day, which shouldn't affect fees or the timetable.
              </small>
            </label>
          </div>

          <div>
            <label htmlFor="hNote" className="block mb-1 font-semibold text-sm">Note</label>
            <InputText
              id="hNote"
              className="w-full"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="Optional"
            />
          </div>

          <div className="flex justify-content-end gap-2 mt-2">
            <Button label="Cancel" text onClick={() => setDialogOpen(false)} />
            <Button label="Save" icon="pi pi-check" loading={saving} onClick={save} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default HolidayCalendar;
